
import { useState, useEffect, useRef, useCallback } from "react";
import { useSupabaseClient } from "@/hooks/useSupabaseClient";
import { useToast } from "@/hooks/use-toast";
import { useUser, useAuth } from "@clerk/clerk-react";
import type { RealtimePostgresChangesPayload, RealtimeChannel } from "@supabase/supabase-js";

import type { Order as AppOrder } from "@/integrations/supabase/types"; // Import the updated Order type
import { ORDER_COLUMNS } from "@/utils/orderUtils";

// Use AppOrder for internal hook state and return type for consistency
export type Order = AppOrder;

export type SortField = "promise_time" | "order_no";
export type SortDirection = "asc" | "desc";

interface UseOrdersProps {
  sortField?: SortField;
  sortDirection?: SortDirection;
  showPendingOnly?: boolean;
  showDeliveryOrders?: boolean;
}

export const useOrders = ({
  sortField = "order_no",
  sortDirection = "desc",
  showPendingOnly = true,
  showDeliveryOrders = false
}: UseOrdersProps = {}) => {
  const getSupabaseClient = useSupabaseClient();
  const { toast } = useToast();
  const { user } = useUser();
  const { getToken } = useAuth();
  const [orders, setOrders] = useState<AppOrder[]>([]); // Use AppOrder for state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const realtimeChannelRef = useRef<RealtimeChannel | null>(null);
  const ordersRef = useRef<AppOrder[]>([]);

  // Keep ordersRef in sync with orders state
  useEffect(() => {
    ordersRef.current = orders;
  }, [orders]);

  const sortOrders = useCallback((ordersToSort: Order[]) => {
    return [...ordersToSort].sort((a, b) => {
      let aValue: string | number | null | undefined;
      let bValue: string | number | null | undefined;

      if (sortField === "promise_time") {
        aValue = a.promise_time;
        bValue = b.promise_time;

        // Handle null values - put them at the end
        if (!aValue && !bValue) return 0;
        if (!aValue) return 1;
        if (!bValue) return -1;

        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      } else if (sortField === "order_no") {
        aValue = a.order_no;
        bValue = b.order_no;

        // Handle null values - put them at the end
        if (aValue === null && bValue === null) return 0;
        if (aValue === null) return 1;
        if (bValue === null) return -1;
      }

      if (sortDirection === "asc") {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
  }, [sortField, sortDirection]);

  const fetchOrders = useCallback(async (showRefreshIndicator = false) => {
    const supabase = await getSupabaseClient();
    if (!supabase) {
      console.log("Supabase client not initialized, returning early");
      // Supabase client is not yet initialized, return early.
      // The useEffect will re-run when it becomes available.
      return;
    }
    try {
      if (showRefreshIndicator) {
        setIsRefreshing(true);
      } else {
        setLoading(true);
      }

      // Get today's date in YYYY-MM-DD format for promise_time filtering
      const today = new Date();
      const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate()).toISOString();
      const todayEnd = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1).toISOString();

      // Get store_id from the Clerk JWT token
      console.log("User metadata:", user?.publicMetadata);

      // Extract store_id from the Clerk JWT token
      let userStoreId = "1"; // Default fallback

      try {
        const jwt = await getToken({ template: "supabase" });

        if (jwt) {
          // Decode the JWT to get the store_id
          const payload = JSON.parse(atob(jwt.split('.')[1]));
          console.log("JWT payload:", payload);

          if (payload.app_metadata?.store_id) {
            userStoreId = payload.app_metadata.store_id;
            console.log("Extracted store_id from JWT:", userStoreId);
          } else {
            console.warn("No store_id found in JWT app_metadata");
          }
        }
      } catch (error) {
        console.error("Error extracting store_id from JWT:", error);
      }

      console.log("Final user store_id:", userStoreId);

      if (!userStoreId) {
        setError('User is not associated with a store.');
        setLoading(false);
        setIsRefreshing(false);
        return;
      }

      console.log("Fetching orders for store_id:", userStoreId, "date range:", todayStart, "to", todayEnd, "pending only:", showPendingOnly);

      let query = supabase
        .from('allorders')
        .select(ORDER_COLUMNS)
        .eq('store_id', userStoreId) // Filter by store_id
        .gte('promise_time', todayStart)
        .lt('promise_time', todayEnd);

      // Add is_posted filter if showPendingOnly is true
      if (showPendingOnly) {
        query = query.eq('is_posted', false);
      }

      // Exclude delivery orders if showDeliveryOrders is false
      if (!showDeliveryOrders) {
        query = query
          .not('order_type', 'ilike', '%delivery%');
      }

      const { data, error } = await query;

      console.log("Supabase response:", { data: data?.length || 0, error });

      if (error) {
        console.error("Supabase error:", error);
        setError(error.message);
        if (!showRefreshIndicator) {
          toast({
            title: "Error",
            description: "Failed to fetch orders from database",
            variant: "destructive",
          });
        }
        return;
      }

      if (!data) {
        setOrders([]);
        setError(null); // Or handle as an error if appropriate
        return;
      }

      // Initial processing (payment_status, etc.)
      const processedOrders: AppOrder[] = data.map(order => ({
        ...order,
        items: null, // Manually set to null as it is excluded from selection
        payment_status: order.payment_amount > 0 ? "Paid" : "Not Paid",
        // Initialize offer fields, they will be populated if offer_id exists
        offer_name: undefined,
        offer_text_sms: undefined,
        offer_value: null,
      })) as AppOrder[]; // Cast to AppOrder[]

      // Fetch offer details for orders that have an offer_id
      // Optimized: Use a single query to fetch all relevant offers instead of N+1 queries
      const ordersWithOffers = processedOrders.filter(order => order.offer_id);

      if (ordersWithOffers.length > 0) {
        // Collect all unique offer IDs
        const offerIds = [...new Set(ordersWithOffers.map(o => o.offer_id).filter(Boolean))] as string[];

        if (offerIds.length > 0) {
          const { data: offerTemplates, error: offerError } = await supabase
            .from('offer_templates')
            .select('offer_id, offer_name, offer_text_sms, offer_value')
            .in('offer_id', offerIds);

          if (offerError) {
            console.error("Error fetching offer templates:", offerError);
          } else if (offerTemplates) {
            // Create a map for O(1) lookup
            const offerMap = new Map(offerTemplates.map(t => [t.offer_id, t]));

            // Update orders with offer details
            processedOrders.forEach(order => {
              if (order.offer_id && offerMap.has(order.offer_id)) {
                const template = offerMap.get(order.offer_id);
                if (template) {
                  order.offer_name = template.offer_name;
                  order.offer_text_sms = template.offer_text_sms;
                  order.offer_value = template.offer_value;
                }
              }
            });
          }
        }
      }

      const sortedOrders = sortOrders(processedOrders);
      setOrders(sortedOrders);
      setError(null);
    } catch (err) {
      setError('Unexpected error occurred');
      if (!showRefreshIndicator) {
        toast({
          title: "Error",
          description: "An unexpected error occurred",
          variant: "destructive",
        });
      }
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, [getSupabaseClient, toast, sortOrders, user, getToken, showPendingOnly, showDeliveryOrders]);

  const handleRealtimeChange = useCallback((payload: RealtimePostgresChangesPayload<AppOrder>) => { // Use AppOrder for payload
    console.log('Realtime change received:', payload);

    // Check if the order is for today based on promise_time
    const orderPromiseTime = (payload.new as AppOrder)?.promise_time || (payload.old as AppOrder)?.promise_time;
    if (orderPromiseTime) {
      const orderDate = new Date(orderPromiseTime);
      const today = new Date();
      const isToday = orderDate.toDateString() === today.toDateString();

      if (!isToday) {
        return; // Ignore orders not for today's promise time
      }
    }

    switch (payload.eventType) {
      case 'INSERT':
        setOrders(prev => {
          const newOrderPayload = payload.new as AppOrder;
          const newOrder = {
            ...newOrderPayload,
            payment_status: newOrderPayload.payment_amount > 0 ? "Paid" : "Not Paid"
          };
          // Check if order already exists to prevent duplicates
          if (prev.some(order => order.order_id === newOrder.order_id)) {
            return prev;
          }
          // Add new order and sort
          const updatedOrders = [newOrder, ...prev];
          return sortOrders(updatedOrders);
        });
        toast({
          title: "New Order",
          description: `New order #${(payload.new as AppOrder).order_no || (payload.new as AppOrder).order_id} received`,
          className: "bg-status-success text-white"
        });
        break;

      case 'UPDATE':
        setOrders(prev => {
          const updatedOrderPayload = payload.new as AppOrder;
          const updatedOrders = prev.map(order =>
            order.order_id === updatedOrderPayload.order_id
              ? {
                ...order,
                ...updatedOrderPayload,
                payment_status: updatedOrderPayload.payment_amount > 0 ? "Paid" : "Not Paid"
              }
              : order
          );
          // Re-sort after update
          return sortOrders(updatedOrders);
        });
        break;

      case 'DELETE':
        setOrders(prev => prev.filter(order => order.order_id !== (payload.old as AppOrder).order_id)); // Use AppOrder for old payload
        break;
    }
  }, [toast, sortOrders]);

  // Re-fetch and sort when sort parameters change
  useEffect(() => {
    setOrders(prev => sortOrders(prev));
  }, [sortOrders]);

  const setupRealtimeSubscription = useCallback(async () => {
    const supabase = await getSupabaseClient();
    if (!supabase) {
      console.warn("Supabase client not initialized for realtime subscription.");
      return;
    }
    // Clean up existing subscription
    if (realtimeChannelRef.current) {
      realtimeChannelRef.current.unsubscribe();
    }

    // Set up new subscription
    realtimeChannelRef.current = supabase
      .channel('allorders-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'allorders',
        },
        handleRealtimeChange
      )
      .subscribe((status) => {
        console.log('Realtime subscription status:', status);
        if (status === 'SUBSCRIBED') {
          console.log('Successfully subscribed to realtime updates');
        } else if (status === 'CHANNEL_ERROR') {
          console.error('Error subscribing to realtime updates');
          toast({
            title: "Connection Issue",
            description: "Real-time updates may not work properly",
            variant: "destructive",
          });
        }
      });
  }, [handleRealtimeChange, toast, getSupabaseClient]);

  const setupPolling = useCallback(() => {
    // Clear existing interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    // Set up polling every 30 seconds as backup
    pollingIntervalRef.current = setInterval(() => {
      fetchOrders(true);
    }, 30000);
  }, [fetchOrders]);

  const handleVisibilityChange = useCallback(() => {
    if (!document.hidden) {
      // Page became visible, refresh orders
      fetchOrders(true);
    }
  }, [fetchOrders]);

  useEffect(() => {
    if (user) {
      // Initial fetch
      fetchOrders();

      // Set up realtime subscription
      setupRealtimeSubscription();

      // Set up polling
      setupPolling();

      // Set up visibility change listener
      document.addEventListener('visibilitychange', handleVisibilityChange);

      // Cleanup
      return () => {
        if (realtimeChannelRef.current) {
          realtimeChannelRef.current.unsubscribe();
        }
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
        }
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    }
  }, [user, getToken, fetchOrders, setupRealtimeSubscription, setupPolling, handleVisibilityChange]);

  // Function to send review request SMS (now internal to useOrders)
  const sendReviewRequestNotification = useCallback(async (order: Order) => {
    const supabase = await getSupabaseClient();
    if (!supabase) {
      console.warn("Supabase client not initialized in sendReviewRequestNotification.");
      return false;
    }
    try {
      // Fetch customer name from customers table using phone number
      let customerName = 'Valued Customer';
      if (order.customer_phone) {
        const { data: customerData, error: customerError } = await supabase
          .from('customers')
          .select('customer_name')
          .eq('customer_phone', order.customer_phone)
          .single();

        if (!customerError && customerData?.customer_name) {
          customerName = customerData.customer_name;
          console.log(`[sendReviewRequestNotification] Found customer name: ${customerName}`);
        } else {
          console.log(`[sendReviewRequestNotification] Customer name not found, using default`);
        }
      }

      const { data, error } = await supabase.functions.invoke('send-notification', {
        body: {
          type: 'ask_for_reviews',
          order_id: order.order_id.toString(),
          order_no: order.order_no?.toString() || order.order_id.toString(),
          customer_phone: order.customer_phone || '',
          customer_name: customerName,
          amount: order.payment_amount?.toString() || order.subtotal?.toString(),
          store_name: "Mountain Mike's", // Consider making dynamic
        }
      });

      if (error) throw error;

      toast({
        title: "Review Request Sent",
        description: `SMS asking for review has been sent for order #${order.order_no}.`,
        className: "bg-status-success text-white"
      });
      return true;
    } catch (error) {
      console.error('Error sending review request notification:', error);
      toast({
        title: "Error",
        description: "Failed to send review request SMS.",
        variant: "destructive",
      });
      return false;
    }
  }, [getSupabaseClient, toast]);

  // Function to update review request sent status (now internal to useOrders)
  const updateReviewRequestSent = useCallback(async (order: Order, sent: boolean) => {
    const supabase = await getSupabaseClient();
    if (!supabase) {
      console.warn("Supabase client not initialized in updateReviewRequestSent.");
      return false;
    }
    try {
      const { error } = await supabase
        .from('allorders')
        .update({
          review_request_sent_notified: sent,
          review_request_sent_timestamp: sent ? new Date().toISOString() : null
        })
        .eq('order_id', order.order_id);

      if (error) {
        console.error('Error updating review request status:', error);
        toast({
          title: "Error",
          description: "Failed to update review request status.",
          variant: "destructive",
        });
        return false;
      }

      if (!error && sent) {
        await sendReviewRequestNotification(order); // Call internal function
      }

      setOrders(prev => {
        const updatedOrders = prev.map(o =>
          o.order_id === order.order_id
            ? {
              ...o,
              review_request_sent_notified: sent,
              review_request_sent_timestamp: sent ? new Date().toISOString() : null
            }
            : o
        );
        return sortOrders(updatedOrders);
      });

      return true;
    } catch (err) {
      console.error('Unexpected error updating review request status:', err);
      toast({
        title: "Error",
        description: "An unexpected error occurred while updating review request status.",
        variant: "destructive",
      });
      return false;
    }
  }, [getSupabaseClient, toast, sendReviewRequestNotification, sortOrders]);

  const sendOrderConfirmationNotification = useCallback(async (order: Order) => {
    const supabase = await getSupabaseClient();
    if (!supabase) {
      console.warn("Supabase client not initialized in sendOrderConfirmationNotification.");
      return false;
    }
    try {
      // Message is now generated by the backend using templates
      const { data, error } = await supabase.functions.invoke('send-notification', {
        body: {
          type: 'order_confirmation',
          manual_trigger: true,
          order_id: order.order_id.toString(),
          order_no: order.order_no?.toString() || order.order_id.toString(),
          customer_phone: order.customer_phone || '',
          customer_name: order.taken_by || 'Valued Customer',
          amount: order.payment_amount?.toString(),
          store_name: "Mountain Mike's", // Hardcoded for now
          promise_time: order.promise_time || '',
          offer_id: order.offer_id || null, // Include offer_id for claim link generation
        }
      });

      if (error) throw error;

      toast({
        title: "Order Confirmation Sent",
        description: "Customer has been notified of order confirmation",
        className: "bg-status-success text-white"
      });

      return true;
    } catch (error) {
      console.error('Error sending order confirmation:', error);
      toast({
        title: "Error",
        description: "Failed to send order confirmation notification",
        variant: "destructive",
      });
      return false;
    }
  }, [getSupabaseClient, toast]);

  const updateOrderConfirmation = useCallback(async (order: Order, confirmed: boolean) => {
    const supabase = await getSupabaseClient();
    if (!supabase) {
      console.warn("Supabase client not initialized in updateOrderConfirmation.");
      return false;
    }
    if (confirmed && !order.offer_id) {
      toast({
        title: "Cannot Send Confirmation",
        description: "This order does not have an offer ID, so a confirmation SMS cannot be sent.",
        variant: "destructive",
      });
      return false;
    }
    try {
      const { error } = await supabase
        .from('allorders')
        .update({
          order_confirmed_notified: confirmed,
          order_confirmed_timestamp: confirmed ? new Date().toISOString() : null
        })
        .eq('order_id', order.order_id);

      if (error) {
        console.error('Error updating order confirmation:', error);
        toast({
          title: "Error",
          description: "Failed to update order confirmation status",
          variant: "destructive",
        });
        return false;
      }

      if (!error && confirmed) {
        await sendOrderConfirmationNotification(order);
      }

      // Update local state immediately for better UX
      setOrders(prev => {
        const updatedOrders = prev.map(o =>
          o.order_id === order.order_id
            ? {
              ...o,
              order_confirmed_notified: confirmed,
              order_confirmed_timestamp: confirmed ? new Date().toISOString() : null
            }
            : o
        );
        // Re-sort after update
        return sortOrders(updatedOrders);
      });

      return true;
    } catch (err) {
      console.error('Unexpected error updating order confirmation:', err);
      return false;
    }
  }, [getSupabaseClient, toast, sendOrderConfirmationNotification, sortOrders]);

  const sendOrderReadyNotification = useCallback(async (order: Order) => {
    const supabase = await getSupabaseClient();
    if (!supabase) {
      console.warn("Supabase client not initialized in sendOrderReadyNotification.");
      return false;
    }
    try {
      // Message is now generated by the backend using templates
      const { data, error } = await supabase.functions.invoke('send-notification', {
        body: {
          type: 'order_ready',
          order_id: order.order_id.toString(),
          order_no: order.order_no?.toString() || order.order_id.toString(),
          customer_phone: order.customer_phone || '',
          customer_name: order.taken_by || 'Valued Customer',
          amount: order.payment_amount?.toString(),
          store_name: "Mountain Mike's", // Hardcoded for now
          promise_time: order.promise_time || '',
          offer_id: order.offer_id || null, // Include offer_id for claim link generation
        }
      });

      if (error) throw error;

      toast({
        title: "Order Ready Notification Sent",
        description: "Customer has been notified that order is ready",
        className: "bg-status-success text-white"
      });

      return true;
    } catch (error) {
      console.error('Error sending order ready notification:', error);
      toast({
        title: "Error",
        description: "Failed to send order ready notification",
        variant: "destructive",
      });
      return false;
    }
  }, [getSupabaseClient, toast]);

  const updateOrderReady = useCallback(async (order: Order, ready: boolean) => {
    const supabase = await getSupabaseClient();
    if (!supabase) {
      console.warn("Supabase client not initialized in updateOrderReady.");
      return false;
    }
    try {
      const { error } = await supabase
        .from('allorders')
        .update({
          order_ready_notified: ready,
          order_ready_timestamp: ready ? new Date().toISOString() : null
        })
        .eq('order_id', order.order_id);

      if (error) {
        console.error('Error updating order ready status:', error);
        toast({
          title: "Error",
          description: "Failed to update order ready status",
          variant: "destructive",
        });
        return false;
      }

      if (!error && ready) {
        await sendOrderReadyNotification(order);
      }

      // Update local state immediately for better UX
      setOrders(prev => {
        const updatedOrders = prev.map(o =>
          o.order_id === order.order_id
            ? {
              ...o,
              order_ready_notified: ready,
              order_ready_timestamp: ready ? new Date().toISOString() : null
            }
            : o
        );
        // Re-sort after update
        return sortOrders(updatedOrders);
      });

      return true;
    } catch (err) {
      console.error('Unexpected error updating order ready status:', err);
      return false;
    }
  }, [getSupabaseClient, toast, sendOrderReadyNotification, sortOrders]);

  const updateOfferSent = useCallback(async (orderId: number, sent: boolean) => {
    const supabase = await getSupabaseClient();
    if (!supabase) {
      console.warn("Supabase client not initialized in updateOfferSent.");
      return false;
    }
    try {
      const { error } = await supabase
        .from('allorders')
        .update({
          offer_sent_notified: sent,
          offer_sent_timestamp: sent ? new Date().toISOString() : null
        })
        .eq('order_id', orderId);

      if (error) {
        console.error('Error updating offer sent status:', error);
        toast({
          title: "Error",
          description: "Failed to update offer sent status",
          variant: "destructive",
        });
        return false;
      }

      // Update local state immediately for better UX
      setOrders(prev => {
        const updatedOrders = prev.map(order =>
          order.order_id === orderId
            ? {
              ...order,
              offer_sent_notified: sent,
              offer_sent_timestamp: sent ? new Date().toISOString() : null
            }
            : order
        );
        // Re-sort after update
        return sortOrders(updatedOrders);
      });

      return true;
    } catch (err) {
      console.error('Unexpected error updating offer sent status:', err);
      return false;
    }
  }, [getSupabaseClient, toast, sortOrders]);

  // Keep the legacy updateOrderNotification function for backward compatibility
  const updateOrderNotification = useCallback(async (orderId: number, notified: boolean) => {
    // Find the order from the local state ref to avoid dependency on orders state
    const orderToUpdate = ordersRef.current.find(o => o.order_id === orderId);

    if (orderToUpdate) {
      return updateOrderConfirmation(orderToUpdate, notified);
    } else {
      console.error(`Order with ID ${orderId} not found in local state. Cannot update notification status.`);
      toast({
        title: "Error",
        description: `Order ${orderId} not found for notification update.`,
        variant: "destructive",
      });
      return false;
    }
  }, [updateOrderConfirmation, toast]); // Removed 'orders' dependency

  const manualRefresh = useCallback(() => {
    fetchOrders(true);
  }, [fetchOrders]);

  return {
    orders,
    loading,
    error,
    isRefreshing,
    refetch: fetchOrders,
    manualRefresh,
    updateOrderNotification,
    updateOrderConfirmation,
    updateOrderReady,
    updateOfferSent,
    sendOrderConfirmationNotification,
    sendOrderReadyNotification,
    updateReviewRequestSent,
    sendReviewRequestNotification
  };
};
